import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Zap, Truck, Wrench, AlertCircle, CheckCircle2, Clock, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TYPE_OPTIONS = [
  { value: 'Breakdown', label: 'Breakdown', color: 'bg-red-500 hover:bg-red-600', hint: 'Asset is down, cannot operate' },
  { value: 'Running Repair', label: 'Running Repair', color: 'bg-amber-500 hover:bg-amber-600', hint: 'Minor fix, no downtime' },
  { value: 'Scheduled Service', label: 'Scheduled Service', color: 'bg-blue-500 hover:bg-blue-600', hint: 'Planned maintenance' },
  { value: 'Long Term Breakdown', label: 'Long Term Breakdown', color: 'bg-slate-800 hover:bg-slate-900', hint: 'Multi-day repair' },
];

function StepPill({ num, label, active, done }) {
  return (
    <div className={`flex items-center gap-2 text-sm ${active ? 'text-slate-900 font-semibold' : 'text-slate-400'}`}>
      <div className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold border-2 ${
        done ? 'bg-emerald-500 border-emerald-500 text-white' :
        active ? 'bg-amber-500 border-amber-500 text-slate-900' :
        'bg-white border-slate-300 text-slate-400'
      }`}>
        {done ? <CheckCircle2 className="h-4 w-4" /> : num}
      </div>
      <span className="hidden sm:inline">{label}</span>
    </div>
  );
}

export default function QuickLogPage({ user }) {
  const navigate = useNavigate();
  const [assets, setAssets] = useState([]);
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [section, setSection] = useState('truck');
  const [doorNo, setDoorNo] = useState('');
  const [type, setType] = useState('');
  const [diagnosis, setDiagnosis] = useState('');
  const [remarks, setRemarks] = useState('');
  const [recentLogs, setRecentLogs] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('token');
        const [assetsRes, logsRes] = await Promise.all([
          axios.get(`${API}/assets`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/maintenance/logs`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        setAssets(assetsRes.data || []);
        const mine = (logsRes.data || [])
          .filter(l => l.created_by === user?.email)
          .slice(0, 5);
        setRecentLogs(mine);
      } catch {
        toast.error('Failed to load assets');
      }
    };
    load();
  }, [user]);

  const sectionAssets = assets.filter(a => {
    const TRUCK_TYPES = ['truck', 'lmv', 'dumper'];
    const isTruck = TRUCK_TYPES.some(t => (a.asset_type || '').toLowerCase().includes(t));
    return section === 'truck' ? isTruck : !isTruck;
  });

  const handleSubmit = async () => {
    if (!doorNo || !type || !diagnosis.trim()) {
      toast.error('Please complete all steps');
      return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        section,
        door_no: doorNo,
        maintenance_type: type,
        reporting_time: new Date().toISOString(),
        diagnosis: diagnosis.trim(),
        parts_required: [],
        remarks: remarks.trim(),
        damage_photos: [],
      };
      await axios.post(`${API}/maintenance/logs`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Logged: ${doorNo} – ${type}`);
      // Reset
      setStep(1);
      setDoorNo('');
      setType('');
      setDiagnosis('');
      setRemarks('');
      // Refresh recent
      const logsRes = await axios.get(`${API}/maintenance/logs`, { headers: { Authorization: `Bearer ${token}` } });
      setRecentLogs((logsRes.data || []).filter(l => l.created_by === user?.email).slice(0, 5));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to log entry');
    } finally {
      setSubmitting(false);
    }
  };

  const canProceed1 = Boolean(doorNo);
  const canProceed2 = Boolean(type);
  const canProceed3 = diagnosis.trim().length >= 5;

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="quick-log-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-xl bg-amber-500/15 border border-amber-500/40 flex items-center justify-center">
          <Zap className="h-5 w-5 text-amber-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Quick Log</h1>
          <p className="text-sm text-slate-600">Report a breakdown or service in under 30 seconds</p>
        </div>
      </div>

      {/* Step indicator */}
      <div className="flex items-center justify-between gap-2 bg-white border border-slate-200 rounded-xl p-3 sm:p-4">
        <StepPill num="1" label="Asset" active={step === 1} done={canProceed1 && step > 1} />
        <div className="flex-1 h-px bg-slate-200" />
        <StepPill num="2" label="Type" active={step === 2} done={canProceed2 && step > 2} />
        <div className="flex-1 h-px bg-slate-200" />
        <StepPill num="3" label="Diagnosis" active={step === 3} done={canProceed3 && step > 3} />
      </div>

      {/* Step 1: Section + Asset */}
      {step === 1 && (
        <div className="bg-white border border-slate-200 rounded-xl p-5 sm:p-6 space-y-5">
          <div>
            <Label className="text-slate-700 mb-2 block">Which section?</Label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => { setSection('truck'); setDoorNo(''); }}
                data-testid="quicklog-section-truck"
                className={`p-4 rounded-xl border-2 flex items-center gap-3 transition-all ${
                  section === 'truck' ? 'border-amber-500 bg-amber-50' : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <Truck className={`h-6 w-6 ${section === 'truck' ? 'text-amber-600' : 'text-slate-500'}`} />
                <div className="text-left">
                  <p className="font-semibold text-slate-900">Truck</p>
                  <p className="text-xs text-slate-500">Tippers, dumpers</p>
                </div>
              </button>
              <button
                type="button"
                onClick={() => { setSection('machinery'); setDoorNo(''); }}
                data-testid="quicklog-section-machinery"
                className={`p-4 rounded-xl border-2 flex items-center gap-3 transition-all ${
                  section === 'machinery' ? 'border-amber-500 bg-amber-50' : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <Wrench className={`h-6 w-6 ${section === 'machinery' ? 'text-amber-600' : 'text-slate-500'}`} />
                <div className="text-left">
                  <p className="font-semibold text-slate-900">Machinery</p>
                  <p className="text-xs text-slate-500">Excavators, loaders</p>
                </div>
              </button>
            </div>
          </div>

          <div>
            <Label className="text-slate-700 mb-2 block">Pick asset (Door No.)</Label>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-[320px] overflow-y-auto p-1">
              {sectionAssets.length === 0 && (
                <p className="col-span-full text-sm text-slate-500 text-center py-8">No assets found in this section</p>
              )}
              {sectionAssets.map(a => (
                <button
                  key={a.id || a.door_no}
                  type="button"
                  onClick={() => setDoorNo(a.door_no)}
                  data-testid={`quicklog-asset-${a.door_no}`}
                  className={`px-3 py-3 rounded-lg border-2 text-sm font-semibold transition-all ${
                    doorNo === a.door_no
                      ? 'border-amber-500 bg-amber-500 text-slate-900'
                      : 'border-slate-200 bg-white text-slate-700 hover:border-slate-400'
                  }`}
                >
                  {a.door_no}
                </button>
              ))}
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              onClick={() => setStep(2)}
              disabled={!canProceed1}
              data-testid="quicklog-next-1"
              className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
            >
              Next: Type →
            </Button>
          </div>
        </div>
      )}

      {/* Step 2: Type */}
      {step === 2 && (
        <div className="bg-white border border-slate-200 rounded-xl p-5 sm:p-6 space-y-5">
          <div>
            <Label className="text-slate-700 mb-3 block">What type of work?</Label>
            <div className="grid sm:grid-cols-2 gap-3">
              {TYPE_OPTIONS.map(t => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => setType(t.value)}
                  data-testid={`quicklog-type-${t.value.replace(/\s/g, '-').toLowerCase()}`}
                  className={`text-white px-4 py-4 rounded-xl text-left transition-all ${t.color} ${
                    type === t.value ? 'ring-4 ring-amber-400 ring-offset-2 scale-[1.02]' : ''
                  }`}
                >
                  <p className="font-bold text-base">{t.label}</p>
                  <p className="text-xs opacity-90 mt-0.5">{t.hint}</p>
                </button>
              ))}
            </div>
          </div>
          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep(1)} data-testid="quicklog-back-2">← Back</Button>
            <Button
              onClick={() => setStep(3)}
              disabled={!canProceed2}
              data-testid="quicklog-next-2"
              className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
            >
              Next: Diagnosis →
            </Button>
          </div>
        </div>
      )}

      {/* Step 3: Diagnosis + submit */}
      {step === 3 && (
        <div className="bg-white border border-slate-200 rounded-xl p-5 sm:p-6 space-y-5">
          <div>
            <Label className="text-slate-700 mb-2 block">What's wrong? (diagnosis)</Label>
            <Textarea
              value={diagnosis}
              onChange={e => setDiagnosis(e.target.value)}
              placeholder="e.g., Hydraulic oil leak from main cylinder seal, needs replacement"
              rows={4}
              data-testid="quicklog-diagnosis"
              className="resize-none"
              autoFocus
            />
            <p className={`text-xs mt-1 ${diagnosis.trim().length < 5 ? 'text-slate-400' : 'text-emerald-600'}`}>
              {diagnosis.trim().length < 5 ? `${5 - diagnosis.trim().length} more characters needed` : '✓ Looks good'}
            </p>
          </div>
          <div>
            <Label className="text-slate-700 mb-2 block">Remarks (optional)</Label>
            <Textarea
              value={remarks}
              onChange={e => setRemarks(e.target.value)}
              placeholder="Any extra notes for the stores/maintenance team..."
              rows={2}
              data-testid="quicklog-remarks"
              className="resize-none"
            />
          </div>

          {/* Summary card */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-sm space-y-1">
            <p><span className="text-slate-500">Section:</span> <span className="font-semibold capitalize">{section}</span></p>
            <p><span className="text-slate-500">Door No:</span> <span className="font-semibold">{doorNo}</span></p>
            <p><span className="text-slate-500">Type:</span> <span className="font-semibold">{type}</span></p>
          </div>

          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep(2)} data-testid="quicklog-back-3">← Back</Button>
            <Button
              onClick={handleSubmit}
              disabled={!canProceed3 || submitting}
              data-testid="quicklog-submit"
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
            >
              {submitting ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Logging…</>
              ) : (
                <><CheckCircle2 className="h-4 w-4 mr-2" /> Submit Log</>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Recent logs by this user */}
      {recentLogs.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              <Clock className="h-4 w-4 text-slate-500" />
              Your Recent Logs
            </h3>
            <Button variant="ghost" size="sm" onClick={() => navigate(`/maintenance/${section}`)} data-testid="quicklog-view-all">
              View all →
            </Button>
          </div>
          <div className="space-y-2">
            {recentLogs.map(log => (
              <div key={log.id} className="flex items-center justify-between text-sm border-b border-slate-100 py-2 last:border-0">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="font-semibold text-slate-900 flex-shrink-0">{log.door_no}</span>
                  <span className="text-slate-500 truncate">{log.diagnosis}</span>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ml-2 ${
                  log.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                  log.maintenance_type?.includes('Breakdown') ? 'bg-red-100 text-red-700' :
                  'bg-amber-100 text-amber-700'
                }`}>
                  {log.status === 'completed' ? 'Closed' : log.maintenance_type}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
